import { useMemo, useState } from "react";
import { Copy, Check, X, Plus, Link2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const SLUG_RE = /^[a-z0-9-]{1,32}$/;

// Normalize as the user types: lowercase, spaces → -, drop disallowed chars,
// truncate to 32. Same rule as the server so preview matches persistence.
export function normalizeSlug(raw) {
  return (raw || "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, 32);
}

async function copyToClipboard(text) {
  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to legacy */
    }
  }
  // Legacy fallback (Safari private mode, no permission, etc.)
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export default function MarketingLinksCard({ channels, onChange }) {
  const [slug, setSlug] = useState("");
  const [showFallback, setShowFallback] = useState(null); // string URL when clipboard fails
  const [justCopied, setJustCopied] = useState(""); // slug that was just copied

  const origin =
    typeof window !== "undefined" ? window.location.origin : "";

  const previewUrl = useMemo(() => {
    if (!slug) return `${origin}/?ref=…`;
    return `${origin}/?ref=${slug}`;
  }, [slug, origin]);

  function onSlugChange(v) {
    setSlug(normalizeSlug(v));
  }

  async function onCopy(text, key) {
    const ok = await copyToClipboard(text);
    if (ok) {
      setJustCopied(key);
      setShowFallback(null);
      toast.success("Copied!");
      setTimeout(() => setJustCopied(""), 2000);
    } else {
      setShowFallback(text);
    }
  }

  function onSave() {
    if (!SLUG_RE.test(slug)) {
      toast.error("Enter a channel name (letters, numbers, dashes).");
      return;
    }
    if (channels.includes(slug)) {
      toast.info("Already in your list.");
      setSlug("");
      return;
    }
    if (channels.length >= 20) {
      toast.error("Max 20 channels.");
      return;
    }
    onChange([...channels, slug]);
    setSlug("");
    toast.success(`Saved "${slug}"`);
  }

  function onRemove(s) {
    onChange(channels.filter((x) => x !== s));
  }

  return (
    <section
      data-testid="marketing-links-card"
      className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5"
    >
      <h2 className="text-xs uppercase tracking-wider font-semibold text-gray-500 flex items-center gap-1.5">
        <Link2 className="w-3.5 h-3.5" /> Marketing Links
      </h2>
      <p className="mt-2 text-sm text-gray-600">
        Build a per-channel tracking link so you can see which source (cards,
        Nextdoor, Google Business Profile…) drives leads.
      </p>

      <div className="mt-4">
        <Label htmlFor="mk-slug" className="text-sm text-[#334E68]">
          Channel name
        </Label>
        <Input
          id="mk-slug"
          data-testid="mk-slug-input"
          value={slug}
          onChange={(e) => onSlugChange(e.target.value)}
          placeholder="e.g. cards, nextdoor, g-business"
          maxLength={64}
          className="mt-1 min-h-[52px] rounded-xl"
        />
        <p className="mt-1 text-xs text-gray-500">
          Letters, numbers and dashes only. Spaces become dashes.
        </p>
      </div>

      <div className="mt-4">
        <Label className="text-xs uppercase tracking-wider font-semibold text-gray-500">
          Preview
        </Label>
        <div className="mt-1 flex gap-2 items-stretch">
          <div
            data-testid="mk-preview-url"
            className="flex-1 min-h-[52px] px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 text-sm text-[#334E68] font-mono break-all flex items-center"
          >
            {previewUrl}
          </div>
          <button
            type="button"
            data-testid="mk-copy-button"
            onClick={() => slug && onCopy(previewUrl, "__preview__")}
            disabled={!slug}
            className="min-h-[52px] px-4 rounded-xl bg-[#334E68] hover:bg-[#293e52] disabled:bg-gray-300 text-white text-sm font-semibold uppercase tracking-wide flex items-center gap-1.5 whitespace-nowrap"
          >
            {justCopied === "__preview__" ? (
              <>
                <Check className="w-4 h-4" /> Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" /> Copy link
              </>
            )}
          </button>
        </div>
        {showFallback && (
          <div className="mt-2" data-testid="mk-copy-fallback">
            <p className="text-xs text-gray-500 mb-1">
              Couldn't copy automatically — select and copy this:
            </p>
            <textarea
              readOnly
              value={showFallback}
              onFocus={(e) => e.target.select()}
              className="w-full text-xs p-2 rounded-lg border border-gray-200 font-mono"
              rows={2}
            />
          </div>
        )}
        <button
          type="button"
          data-testid="mk-save-button"
          onClick={onSave}
          disabled={!SLUG_RE.test(slug)}
          className="mt-3 min-h-[44px] px-4 rounded-xl bg-white border border-gray-200 hover:border-gray-300 disabled:opacity-40 text-[#334E68] text-sm font-semibold uppercase tracking-wide inline-flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> Save to my list
        </button>
      </div>

      <div className="mt-5">
        <Label className="text-xs uppercase tracking-wider font-semibold text-gray-500">
          Saved channels
        </Label>
        <div
          data-testid="mk-saved-channels"
          className="mt-2 flex flex-wrap gap-2"
        >
          {(!channels || channels.length === 0) && (
            <span className="text-xs text-gray-500">
              None yet. Save your first channel above.
            </span>
          )}
          {channels?.map((c) => {
            const url = `${origin}/?ref=${c}`;
            const copied = justCopied === c;
            return (
              <span
                key={c}
                data-testid={`mk-chip-${c}`}
                className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-gray-100 text-sm text-[#334E68]"
              >
                {c}
                <button
                  type="button"
                  aria-label={`Copy link for ${c}`}
                  data-testid={`mk-chip-copy-${c}`}
                  onClick={() => onCopy(url, c)}
                  className="w-7 h-7 rounded-full hover:bg-gray-200 flex items-center justify-center"
                >
                  {copied ? (
                    <Check className="w-3.5 h-3.5 text-[#16A34A]" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${c}`}
                  data-testid={`mk-chip-remove-${c}`}
                  onClick={() => onRemove(c)}
                  className="w-7 h-7 rounded-full hover:bg-gray-200 flex items-center justify-center"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}
